import { useCallback, useEffect, useSyncExternalStore, type ReactNode } from 'react';

export {
  matchPath,
  resolveRoute,
  chooseSurfaces,
  type MatchedRoute,
  type RouteParams,
  type Surface,
} from './registry.js';

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (options.replace === true) window.history.replaceState({}, '', to);
  else window.history.pushState({}, '', to);
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function usePathname(): string {
  const pathname = useSyncExternalStore(
    subscribe,
    () => window.location.pathname,
    () => '/',
  );

  useEffect(() => {
    window.addEventListener('popstate', notify);
    return () => window.removeEventListener('popstate', notify);
  }, []);

  return pathname;
}

export function useNavigate(): (to: string, options?: { replace?: boolean }) => void {
  return useCallback((to: string, options?: { replace?: boolean }) => navigate(to, options ?? {}), []);
}

export function Link({ to, children, className }: { to: string; children: ReactNode; className?: string }): ReactNode {
  return (
    <a
      href={to}
      className={className}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}
